import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import {fetchRepositoryFromServer} from '@github-ui/issues-repository-picker'
import {OrgRepositoryPicker, type OrgRepositoryPickerRepository} from '@github-ui/item-picker/OrgRepositoryPicker'
import {
  changeDestinationLabel,
  destinationAccessibleName,
  RepositoryDestinationVisual,
} from '@github-ui/item-picker/OrgRepositoryPickerVisuals'
import {noop} from '@github-ui/noop'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {CommandIconButton, GlobalCommands} from '@github-ui/ui-commands'
import {ArrowLeftIcon, CopyIcon, ScreenFullIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, IconButton, VisuallyHidden} from '@primer/react'
import type {DialogHeaderProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useCallback, useEffect, useMemo, useRef} from 'react'

import {BUTTON_LABELS} from '../constants/buttons'
import {ERRORS} from '../constants/errors'
import {LABELS} from '../constants/labels'
import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {
  useIssueCreateActions,
  useIssueCreateComposition,
  useIssueCreateMetadata,
} from '../contexts/IssueCreateDataContext'
import {DisplayMode, getRepositoryDisplayMode} from '../utils/display-mode'
import {instanceOfIssueFormData, IssueCreationKind} from '../utils/model'
import {appendChooseToBasePath} from '../utils/urls'
import styles from './CreateIssueDialogHeader.module.css'

type CreateIssueDialogHeaderProps = {
  navigate: (url: string) => void
  issueFormRef?: RefObject<IssueFormRef | null>
} & DialogHeaderProps

export const CreateIssueDialogHeader = ({
  navigate,
  issueFormRef,
  dialogLabelId,
  onClose,
}: CreateIssueDialogHeaderProps) => {
  const {displayMode, setDisplayMode, optionConfig, isSubIssue} = useIssueCreateConfigContext()
  const {repository, repositoryAbsolutePath, template, setTemplate, setRepository, usedStorageKeyPrefix} =
    useIssueCreateMetadata()
  const {title, body, issueFormBody, setTitle, setBody} = useIssueCreateComposition()
  const {resetMetadata} = useIssueCreateActions()
  const {addToast} = useToastContext()
  const defaultIssueCreateRepoEnabled = isFeatureEnabled('memex_default_issue_create_repository')

  // Title/body drafts are stored per template (usedStorageKeyPrefix includes the template file
  // name). Switching repos clears the template, which re-keys the draft to the blank namespace and
  // hides what the user typed. Capture the current draft here and re-apply it after the re-key so a
  // repo switch never appears to discard in-progress content.
  const pendingDraftRef = useRef<{title: string; body: string} | null>(null)

  // The repo fetch below is async; the user can keep typing while it is in flight. Read the draft
  // from this ref after the await so we capture the latest title/body/template, not the values
  // captured when the callback was created.
  const compositionRef = useRef({title, body, issueFormBody, template})
  useEffect(() => {
    compositionRef.current = {title, body, issueFormBody, template}
  }, [title, body, issueFormBody, template])

  // Each repo switch starts an uncancelled lookup. Track the latest request so a slower earlier
  // response can't overwrite a newer selection when the user switches repos in quick succession.
  const selectionRequestRef = useRef(0)

  useEffect(() => {
    const pending = pendingDraftRef.current
    if (!pending) return
    pendingDraftRef.current = null
    setTitle(pending.title)
    setBody(pending.body)
  }, [usedStorageKeyPrefix, setTitle, setBody])

  const onRepositorySwitched = useCallback(
    (selection: OrgRepositoryPickerRepository | undefined) => {
      if (!selection) return
      const {ownerLogin: owner, name} = selection
      void (async () => {
        const requestId = (selectionRequestRef.current += 1)
        let repo
        try {
          repo = await fetchRepositoryFromServer(owner, name)
        } catch {
          repo = null
        }
        // A newer selection started while this fetch was in flight; discard this stale result.
        if (requestId !== selectionRequestRef.current) return
        if (!repo) {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({type: 'error', message: ERRORS.unableToLoadCurrentRepository})
          return
        }
        if (repository && repo.id === repository.id) return

        // Preserve what the user has already typed across the switch, and keep them on the form
        // rather than bouncing back to template selection once they've started composing. Read the
        // latest draft from the ref so anything typed while the fetch was in flight is kept.
        const {
          title: currentTitle,
          body: currentBody,
          issueFormBody: currentIssueFormBody,
          template: currentTemplate,
        } = compositionRef.current
        // Issue-form answers live in the form ref and are only serialized to Markdown on demand, so
        // read the live Markdown from the ref (the same source used at submission) before the form
        // unmounts. issueFormBody is only kept in sync while duplicate detection is enabled, so
        // reading it alone would silently drop answers whenever that flag is off.
        const isIssueForm = Boolean(currentTemplate && instanceOfIssueFormData(currentTemplate.data))
        const preservedBody = isIssueForm ? (issueFormRef?.current?.markdown() ?? currentIssueFormBody) : currentBody
        const hasComposed = Boolean(currentTitle?.trim() || preservedBody?.trim() || currentTemplate)
        if (hasComposed) pendingDraftRef.current = {title: currentTitle, body: preservedBody}

        resetMetadata({repository, selectedRepository: repo})
        setTemplate(undefined)
        setDisplayMode(
          hasComposed ? DisplayMode.IssueCreation : (getRepositoryDisplayMode(repo) ?? DisplayMode.IssueCreation),
        )
        setRepository(repo)
      })()
    },
    [repository, resetMetadata, setDisplayMode, setRepository, setTemplate, addToast, issueFormRef],
  )

  const onCloseClick = useCallback(() => {
    onClose('close-button')
  }, [onClose])

  const chooseUrlForCopy = useMemo(() => appendChooseToBasePath(repositoryAbsolutePath), [repositoryAbsolutePath])

  // For template selection, we want to show a "Copy URL" button, while for issue creation we can show a fullscreen navigation.
  const showCopyUrlButton = displayMode === DisplayMode.TemplatePicker && repository !== undefined
  const showFullScreenButton =
    displayMode === DisplayMode.IssueCreation &&
    optionConfig.showFullScreenButton &&
    navigate !== noop &&
    repository !== undefined

  const handleBackToTemplates = useCallback(() => {
    // Clear metadata with preservation logic for sub-issues
    resetMetadata({
      repository,
      selectedRepository: repository, // Staying in same repository when going back to templates
    })

    setTemplate(undefined)
    setDisplayMode(DisplayMode.TemplatePicker)
  }, [resetMetadata, setTemplate, setDisplayMode, repository])

  const navigateToFullscreen = () => {
    if (!showFullScreenButton) {
      return
    }

    const baseUrl = `/${repository.nameWithOwner}/issues/new`
    const templateFileName = !template || template.kind === IssueCreationKind.BlankIssue ? undefined : template.fileName
    const link = templateFileName ? `${baseUrl}?template=${encodeURIComponent(templateFileName)}` : baseUrl
    navigate(link)
  }

  const repositoryNameWithOwner = repository?.nameWithOwner

  // Show the repository switcher in the header for both the template-picker and issue-creation panes
  // (only where the flow already allows changing the repository). Repo-pinned flows set
  // `showRepositoryPicker: false`, so the switcher stays out of repo-level dialogs.
  const showRepoSwitcher =
    optionConfig.showOrgRepositoryPicker &&
    optionConfig.showRepositoryPicker &&
    (displayMode === DisplayMode.TemplatePicker || displayMode === DisplayMode.IssueCreation) &&
    repository !== undefined

  const switcherSelection = useMemo<OrgRepositoryPickerRepository | undefined>(
    () =>
      repository
        ? {
            id: repository.id,
            name: repository.name,
            nameWithOwner: repository.nameWithOwner,
            ownerLogin: repository.owner.login,
            isPrivate: repository.isPrivate,
          }
        : undefined,
    [repository],
  )

  const dialogTitle = useMemo(() => {
    if (optionConfig.dialogTitle) return optionConfig.dialogTitle

    // If we are adding this as a child of another issue, then we want to update the label to match
    const relationType = isSubIssue ? 'sub-issue' : 'issue'

    if (displayMode === DisplayMode.TemplatePicker || !repositoryNameWithOwner) {
      const initialTitle = optionConfig.issueCreateArguments?.initialValues?.title
      return LABELS.issueCreateDialogTitleTemplatePane(relationType) + (initialTitle ? ` - "${initialTitle}"` : '')
    }

    // If it's a blank issue then we don't want to show the template suffix
    const templateName = !template || template.kind === IssueCreationKind.BlankIssue ? undefined : template.name

    if (displayMode === DisplayMode.IssueDuplication) {
      return LABELS.issueCreateDialogTitleDuplicationPane(repositoryNameWithOwner, relationType)
    }

    return LABELS.issueCreateDialogTitleCreationPane(repositoryNameWithOwner, templateName, relationType)
  }, [displayMode, isSubIssue, repositoryNameWithOwner, template, optionConfig])

  return (
    <Dialog.Header>
      <GlobalCommands commands={{'issue-create:open-fullscreen': navigateToFullscreen}} />
      <div className={styles.CreateIssueDialogHeaderContainer}>
        {(defaultIssueCreateRepoEnabled || showRepoSwitcher) && displayMode === DisplayMode.IssueCreation && (
          <IconButton
            icon={ArrowLeftIcon}
            aria-label="Back to templates"
            variant="invisible"
            onClick={handleBackToTemplates}
            className={showRepoSwitcher ? styles.backButton : styles.CopyToClipboardIconButton}
          />
        )}
        <div
          className={
            showRepoSwitcher && switcherSelection
              ? `${styles.dialogTitleSection} ${styles.dialogTitleSectionWithSwitcher}`
              : styles.dialogTitleSection
          }
        >
          {showRepoSwitcher && switcherSelection ? (
            <div className={styles.titleSwitcherRow}>
              {/* Type-qualified names (including visibility) so the destination isn't read from the icon alone. */}
              <Dialog.Title id={dialogLabelId}>
                Create new issue in <VisuallyHidden>{destinationAccessibleName(switcherSelection)}</VisuallyHidden>
              </Dialog.Title>
              <OrgRepositoryPicker
                initialSelection={switcherSelection}
                onSelect={onRepositorySwitched}
                scope={optionConfig.scopedRepositoryOwner}
                anchorElement={anchorProps => (
                  <Button
                    className={styles.switcherButton}
                    variant="invisible"
                    size="small"
                    leadingVisual={() => <RepositoryDestinationVisual repository={switcherSelection} />}
                    trailingVisual={TriangleDownIcon}
                    aria-label={changeDestinationLabel(switcherSelection)}
                    {...anchorProps}
                  >
                    {switcherSelection.nameWithOwner}
                  </Button>
                )}
              />
            </div>
          ) : (
            <Dialog.Title id={dialogLabelId}>{dialogTitle}</Dialog.Title>
          )}
        </div>
        {showCopyUrlButton && (
          <CopyToClipboardIconButton
            textToCopy={chooseUrlForCopy}
            ariaLabel={BUTTON_LABELS.copyUrl}
            icon={CopyIcon}
            tooltipProps={{direction: 'n'}}
            className={styles.CopyToClipboardIconButton}
          />
        )}
        {showFullScreenButton && (
          <CommandIconButton
            variant="invisible"
            commandId="issue-create:open-fullscreen"
            icon={ScreenFullIcon}
            className={styles.CopyToClipboardIconButton}
          />
        )}
        <Dialog.CloseButton onClose={onCloseClick} />
      </div>
    </Dialog.Header>
  )
}
